/* eslint-disable @typescript-eslint/no-explicit-any */
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  useEffect,
  useState,
  useRef,
  useCallback,
  lazy,
  Suspense,
} from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { ChevronLeft, ChevronRight, RotateCcw } from 'lucide-react';
import { cn } from '@/lib/utils';
import { debugLog } from '@/lib/debug';
import { useTripPreferences } from '@/hooks/useTripPreferences';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import { motion, AnimatePresence } from 'motion/react';

// Import step components
import DatesStep from './plan/components/DatesBudgetStep';
import InterestsStep from './plan/components/InterestsStep';
import ReviewStep from './plan/components/ReviewStep';
import MobileStepIndicator from './plan/components/MobileStepIndicator';

// Import types
import { type FormStep, ALL_FORM_STEPS } from './plan/types';

// Wrapper component to handle authentication
function TripPlanningWrapper() {
  return (
    <ProtectedRoute>
      <TripPlanningPage />
    </ProtectedRoute>
  );
}

// Use lazy loading for the component
const TripPlanningPageLazy = lazy(() =>
  Promise.resolve({ default: TripPlanningWrapper })
);

export const Route = createFileRoute('/trip/plan')({
  component: () => (
    <Suspense
      fallback={
        <div className="flex items-center justify-center min-h-screen">
          <Progress value={30} className="w-56 h-2" />
          <span className="ml-2 text-sm text-slate-600">
            Loading trip planner...
          </span>
        </div>
      }
    >
      <TripPlanningPageLazy />
    </Suspense>
  ),
});

function TripPlanningPage() {
  const [currentStep, setCurrentStep] = useState<FormStep>('dates');
  const [completedSteps, setCompletedSteps] = useState<Set<FormStep>>(
    new Set()
  );
  const [validationError, setValidationError] = useState<string | null>(null);

  const {
    preferences,
    updateDates,
    updateActivityPreferences,
    hasInitialPreferences,
    clearInitialPreferences,
    resetPreferences,
  } = useTripPreferences();

  const navigate = useNavigate();

  // Handler to reset trip and go back to landing page
  const handleStartNewTrip = useCallback(() => {
    resetPreferences();
    clearInitialPreferences();
    navigate({ to: '/' });
  }, [resetPreferences, clearInitialPreferences, navigate]);

  // Only clear initial preferences on unmount, but DON'T reset preferences
  // if they came from the landing page (hasInitialPreferences = true means we have
  // preferences from the landing page that we want to preserve)
  useEffect(() => {
    // Cleanup: clear initial preferences when unmounting
    return () => {
      clearInitialPreferences();
    };
  }, [clearInitialPreferences]);

  debugLog('log', 'preferences', preferences);

  // MCP integration state
  const [isGeneratingItinerary, setIsGeneratingItinerary] = useState(false);
  const [generationError, setGenerationError] = useState<string | null>(null);
  const [streamingUpdates, setStreamingUpdates] = useState<
    Array<{ id: string; message: string; [key: string]: unknown }>
  >([]);
  const streamingContainerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new streaming updates arrive
  useEffect(() => {
    if (streamingContainerRef.current && streamingUpdates.length > 0) {
      streamingContainerRef.current.scrollTop =
        streamingContainerRef.current.scrollHeight;
    }
  }, [streamingUpdates]);

  // Calculate progress
  const currentStepIndex = ALL_FORM_STEPS.findIndex(
    step => step.id === currentStep
  );
  const progress = ((currentStepIndex + 1) / ALL_FORM_STEPS.length) * 100;

  // Navigation functions - memoized
  const goToNextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex < ALL_FORM_STEPS.length) {
      // If we're on the interests step and trying to go to review, validate activity preferences
      if (currentStep === 'interests') {
        const hasActivityPreferences =
          !!preferences.activityPreferences?.interests?.length;
        if (!hasActivityPreferences) {
          // Show a validation error instead of an alert
          setValidationError(
            'Please select at least one activity category before proceeding.'
          );
          return;
        }
      }

      // Clear any validation errors when moving to next step
      setValidationError(null);
      setCompletedSteps(prev => new Set([...prev, currentStep]));
      setCurrentStep(ALL_FORM_STEPS[nextIndex].id);
    }
  }, [currentStepIndex, currentStep, preferences]);

  const goToPreviousStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex >= 0) {
      setCurrentStep(ALL_FORM_STEPS[prevIndex].id);
    }
  }, [currentStepIndex]);

  const goToStep = useCallback(
    (stepId: FormStep) => {
      // If trying to navigate directly to review step, validate that activity preferences exist
      if (stepId === 'review') {
        const hasActivityPreferences =
          (preferences.activityPreferences?.interests?.length || 0) >= 2;
        if (!hasActivityPreferences) {
          // Set current step to interests step
          setCurrentStep('interests');
          // Show validation error
          setValidationError(
            'Please select at least 2 activity categories before proceeding to review.'
          );
          return;
        }
      }

      setCurrentStep(stepId);
    },
    [preferences, setValidationError]
  );

  // Step validation - memoized
  const isStepValid = useCallback(
    (stepId: string): boolean => {
      switch (stepId) {
        case 'dates':
          return !!(preferences.dates.start && preferences.dates.end);
        case 'interests':
          return (preferences.activityPreferences?.interests?.length || 0) >= 2;
        default:
          return true;
      }
    },
    [preferences]
  );

  const canProceed = isStepValid(currentStep);

  // Render each step with independent Suspense boundaries for better performance
  const renderStep = useCallback(() => {
    switch (currentStep) {
      case 'dates':
        return (
          <DatesStep
            preferences={preferences}
            startDate={preferences.dates.start}
            setStartDate={(date: Date) => updateDates({ start: date })}
            endDate={preferences.dates.end}
            setEndDate={(date: Date) => updateDates({ end: date })}
          />
        );
      case 'interests':
        return (
          <InterestsStep
            preferences={preferences}
            updateInterests={updateActivityPreferences}
            validationError={validationError}
          />
        );
      case 'review':
        return (
          <ReviewStep
            preferences={preferences}
            startDate={preferences.dates.start}
            endDate={preferences.dates.end}
            updateDates={(dates: { startDate: Date; endDate: Date }) =>
              updateDates({ start: dates.startDate, end: dates.endDate })
            }
            isGeneratingItinerary={isGeneratingItinerary}
            setIsGeneratingItinerary={setIsGeneratingItinerary}
            generationError={generationError}
            setGenerationError={setGenerationError}
            streamingUpdates={streamingUpdates}
            setStreamingUpdates={setStreamingUpdates}
            streamingContainerRef={streamingContainerRef}
          />
        );
      default:
        return (
          <DatesStep
            preferences={preferences}
            startDate={preferences.dates.start}
            setStartDate={(date: Date) => updateDates({ start: date })}
            endDate={preferences.dates.end}
            setEndDate={(date: Date) => updateDates({ end: date })}
          />
        );
    }
  }, [
    currentStep,
    updateActivityPreferences,
    updateDates,
    isGeneratingItinerary,
    setIsGeneratingItinerary,
    generationError,
    setGenerationError,
    streamingUpdates,
    setStreamingUpdates,
    streamingContainerRef,
    validationError,
    preferences,
  ]);

  return (
    <div className="min-h-screen bg-background py-8">
      <div className="max-w-4xl mx-auto px-4">
        {/* Mobile Progress Indicator */}
        <MobileStepIndicator
          currentStep={currentStep}
          completedSteps={completedSteps}
          goToStep={goToStep}
          formSteps={ALL_FORM_STEPS as unknown as (typeof ALL_FORM_STEPS)[number][]}
          destinationCity={preferences?.destination?.city}
          onStartNewTrip={handleStartNewTrip}
        />

        {/* Desktop Progress Bar */}
        <div className="hidden md:block mb-8">
          <div className="flex justify-between items-center mb-4">
            <h1 className="text-3xl font-bold text-foreground">
              Plan Your Trip
            </h1>
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleStartNewTrip}
                className="border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/40"
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Start New Trip
              </Button>
              <span className="text-sm text-muted-foreground">
                Step {currentStepIndex + 1} of {ALL_FORM_STEPS.length}
              </span>
            </div>
          </div>
          <div className="w-full h-2 bg-amber-100 dark:bg-amber-900/40 rounded-full overflow-hidden">
            <div
              className="h-2 bg-amber-500 dark:bg-amber-400 rounded-full transition-all"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>

        {/* Step Navigation - Desktop only */}
        <div className="hidden md:block mb-8">
          <div className="flex flex-wrap gap-2">
            {ALL_FORM_STEPS.map(step => (
              <button
                key={step.id}
                onClick={() => goToStep(step.id)}
                className={cn(
                  'px-3 py-2 rounded-md text-sm font-medium transition-colors',
                  currentStep === step.id
                    ? 'bg-amber-600 text-white'
                    : completedSteps.has(step.id)
                      ? 'bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200'
                      : 'bg-muted text-muted-foreground'
                )}
              >
                {step.title}
              </button>
            ))}
          </div>
        </div>

        {/* Current Step Content */}
        <Card className="mb-4 bg-card border-border rounded-lg shadow-md">
          <CardHeader className="md:block">
            <CardTitle className="text-foreground hidden md:block">
              {(() => {
                const stepTitle = ALL_FORM_STEPS.find(step => step.id === currentStep)?.title || '';
                const city = preferences?.destination?.city;
                if (city && currentStep !== 'review') {
                  return `${city} ${stepTitle}`;
                }
                return stepTitle;
              })()}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -16 }}
                transition={{ duration: 0.3 }}
              >
                {renderStep()}
              </motion.div>
            </AnimatePresence>
          </CardContent>
        </Card>

        {/* Navigation Buttons */}
        <div className="flex justify-between">
          <Button
            variant="outline"
            onClick={goToPreviousStep}
            disabled={currentStepIndex === 0}
            className="border-amber-500 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/40"
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Previous</span>
          </Button>

          <Button
            onClick={goToNextStep}
            disabled={!canProceed || currentStepIndex === ALL_FORM_STEPS.length - 1}
            className="bg-amber-600 hover:bg-amber-700 text-white dark:bg-amber-500 dark:hover:bg-amber-600"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="h-4 w-4 ml-2" />
          </Button>
        </div>
      </div>
    </div>
  );
}
